import React from "react";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import { hasItems, takeFirst } from "~/features/app/utils";
import { Button } from "~/features/ui/button";
import { Link } from "~/features/ui/link";
import { isLinkValid } from "~/features/ui/link/utils";
import { useLinkIcon } from "~/features/ui/link/use-link-icon";
import { ProductCard } from "~/features/card/product-card";
import { ProductFeaturedCard } from "~/features/card/product-featured-card";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { up } from "~/features/app/screens";
import { AdaptiveSection } from "./adaptive-section";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type HighlightedProductsRenderContext = {
  count: number;
  isDesktop: boolean;
  mode: Mode;
};
export type HighlightedProductsBlockViewProps<TProduct = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  generations?: (string | null)[];
  getProductKey?: (product: TProduct, index: number) => React.Key;
  hasFilter?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string | null;
  isHighlighted?: boolean | null;
  link?: React.ReactNode;
  mode?: string | null;
  products?: TProduct[] | null;
  renderProduct: (product: TProduct, index: number, context: HighlightedProductsRenderContext) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  text?: string | null;
  theme?: string | null;
};
export function HighlightedProductsBlockView<TProduct = unknown>({
  anchor,
  generations,
  getProductKey,
  hasFilter = false,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  link,
  mode = "light",
  products = [],
  renderProduct,
  sectionProps,
  text,
  theme = "white"
}: HighlightedProductsBlockViewProps<TProduct>) {
  const isDesktop = useMediaQuery(up("lg"));
  const items = products ?? [];
  if (!hasItems(items)) {
    return null;
  }
  const count = items.length;
  const context: HighlightedProductsRenderContext = {
    count,
    isDesktop,
    mode: mode as Mode
  };
  return <AdaptiveSection {...sectionProps} mode={mode as Mode} theme={theme as Theme} headline={headline || undefined} text={text || undefined} link={link} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} hasTopDivider={hasTopDivider ?? true} isHighlighted={isHighlighted ?? false} generations={hasFilter ? generations : undefined}>
      {items.map((product, index) => <React.Fragment key={getProductKey?.(product, index) ?? index}>
          {renderProduct(product, index, context)}
        </React.Fragment>)}
    </AdaptiveSection>;
}